import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Define available currencies
export type CurrencyCode = 'USD' | 'GBP' | 'EUR' | 'CAD' | 'AUD' | 'INR' | 'JPY';

export interface Currency {
  code: CurrencyCode;
  symbol: string;
  name: string;
  exchangeRate: number; // Exchange rate relative to USD (e.g., 1 USD = 0.79 GBP)
}

// Define available currencies with their properties
export const currencies: Record<CurrencyCode, Currency> = {
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', exchangeRate: 1 },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound', exchangeRate: 0.79 },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', exchangeRate: 0.93 },
  CAD: { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar', exchangeRate: 1.37 },
  AUD: { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', exchangeRate: 1.52 },
  INR: { code: 'INR', symbol: '₹', name: 'Indian Rupee', exchangeRate: 83.14 },
  JPY: { code: 'JPY', symbol: '¥', name: 'Japanese Yen', exchangeRate: 154.8 },
};

// Map country codes to currency codes
export const countryCurrencyMap: Record<string, CurrencyCode> = {
  // North America
  US: 'USD',
  CA: 'CAD',
  // Europe
  GB: 'GBP',
  UK: 'GBP', // Sometimes UK is used instead of GB
  DE: 'EUR',
  FR: 'EUR',
  IT: 'EUR',
  ES: 'EUR',
  NL: 'EUR',
  BE: 'EUR',
  IE: 'EUR',
  FI: 'EUR',
  AT: 'EUR',
  GR: 'EUR',
  PT: 'EUR',
  // Asia-Pacific
  AU: 'AUD',
  NZ: 'AUD',
  IN: 'INR',
  JP: 'JPY',
  // Default for countries not listed will be USD
};

interface CurrencyContextType {
  currentCurrency: Currency;
  setCurrentCurrency: (currencyCode: CurrencyCode) => void;
  availableCurrencies: Currency[];
  formatCurrency: (amount: number) => string;
  convertFromUSD: (amountInUSD: number) => number;
}

// Create the context with default value
const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  // Default to USD
  const [currentCurrency, setCurrentCurrency] = useState<Currency>(currencies.USD);
  const [geoLocationChecked, setGeoLocationChecked] = useState<boolean>(false);

  // Function to detect location and set currency
  const detectLocationAndSetCurrency = async () => {
    try {
      // Use a free geolocation API
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      
      // Get country code from the response
      const countryCode = data.country_code;
      
      // Find matching currency or default to USD
      const currencyCode = countryCurrencyMap[countryCode] || 'USD';
      setCurrentCurrency(currencies[currencyCode]);
    } catch (error) {
      console.error('Error detecting location:', error);
      // Default to USD on error
      setCurrentCurrency(currencies.USD);
    } finally {
      setGeoLocationChecked(true);
    }
  };

  // Detect location on initial load
  useEffect(() => {
    if (!geoLocationChecked) {
      detectLocationAndSetCurrency();
    }
  }, [geoLocationChecked]);

  // Set currency based on currency code
  const setCurrency = (currencyCode: CurrencyCode) => {
    setCurrentCurrency(currencies[currencyCode]);
  };

  // Convert amount from USD to current currency
  const convertFromUSD = (amountInUSD: number): number => {
    return amountInUSD * currentCurrency.exchangeRate;
  };

  // Format currency based on current selection
  const formatCurrency = (amount: number): string => {
    // Convert amount to current currency
    const convertedAmount = convertFromUSD(amount);
    
    // Format based on currency settings
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currentCurrency.code,
      minimumFractionDigits: currentCurrency.code === 'JPY' ? 0 : 2,
      maximumFractionDigits: currentCurrency.code === 'JPY' ? 0 : 2,
    }).format(convertedAmount);
  };

  // Get all available currencies as array
  const availableCurrencies = Object.values(currencies);

  return (
    <CurrencyContext.Provider value={{
      currentCurrency,
      setCurrentCurrency: setCurrency,
      availableCurrencies,
      formatCurrency,
      convertFromUSD,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

// Custom hook to use the currency context
export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
